<template>
  <aside class="deluxe-sidebar d-print-none" :class="{ 'deluxe-sidebar--collapsed': collapsed }">
    <div class="deluxe-sidebar__inner">
      <div class="deluxe-sidebar__brand">
        <router-link to="/" class="deluxe-sidebar__logo" title="LENS">
          <img class="deluxe-sidebar__logo-mark" src="/lens-logo-min.svg" alt="LENS" />
          <img class="hide-on-minimized deluxe-sidebar__brand-wordmark" src="/lens-pro-wordmark.svg" alt="Lens PRO" />
        </router-link>
        <button type="button" class="deluxe-sidebar__collapse" title="Toggle sidebar" @click="$emit('toggle-collapse')">
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M19.1088 19.1088C17.7175 20.5 15.4783 20.5 11 20.5C6.52166 20.5 4.28249 20.5 2.89124 19.1088C1.5 17.7175 1.5 15.4783 1.5 11C1.5 6.52166 1.5 4.28248 2.89124 2.89124C4.28249 1.5 6.52166 1.5 11 1.5C15.4783 1.5 17.7175 1.5 19.1088 2.89124C20.5 4.28249 20.5 6.52166 20.5 11C20.5 15.4783 20.5 17.7175 19.1088 19.1088Z" stroke="#777777" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
            <path d="M8 20.5L8 1.5" stroke="#777777" stroke-width="1.5"></path>
          </svg>
        </button>
      </div>

      <div class="link-wrapper">
        <p class="link-wrapper__title">{{ $t('Projects') }}</p>
        <ul class="link-wrapper__links">
          <li v-for="item in mainLinks" :key="item.key" :class="itemClass(item)">
            <a :href="item.href || '#'" :title="item.label" @click="onItemClick($event, item)">
              <i :class="item.icon"></i>
              <span class="hide-on-minimized">{{ item.label }}</span>
              <i
                v-if="item.children"
                :class="[
                  'fa-solid sidebar-dropdown__chev hide-on-minimized',
                  isChevronOpen(item) ? 'fa-chevron-up' : 'fa-chevron-down'
                ]"
              ></i>
            </a>

            <div v-if="item.children && !item.flyout" class="sidebar-dropdown__menu">
              <a
                v-for="child in item.children"
                :key="child.key"
                :href="child.href"
                :class="{ active: isItemActive(child) }"
                @click="onItemClick($event, child)"
              >
                {{ child.label }}
              </a>
            </div>
          </li>
        </ul>
      </div>

      <div class="link-wrapper deluxe-sidebar__bottom">
        <p class="link-wrapper__title">{{ $t('Others') }}</p>
        <ul class="link-wrapper__links">
          <li>
            <a href="#" title="LENS GPT">
              <i class="fa-solid fa-display"></i>
              <span class="hide-on-minimized">LENS GPT</span>
            </a>
          </li>
          <li>
            <a href="#" :title="$t('Logout')" @click.prevent="logoutUser">
              <i class="fa-solid fa-right-from-bracket"></i>
              <span class="hide-on-minimized">{{ $t('Logout') }}</span>
            </a>
          </li>
        </ul>

        <div class="account-links">
          <a href="#" class="account-links__wrapper" :title="accountTitle">
            <span class="account-links__img">
              <i class="fa-regular fa-user"></i>
            </span>
            <span class="account-links__details hide-on-minimized">
              <span class="account-name">{{ accountTitle }}</span>
              <span class="account-email">{{ userInfo.email || '' }}</span>
            </span>
          </a>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="activeFlyoutItem" class="sidebar-flyout" :style="flyoutStyle" @click.stop>
        <a
          v-for="child in activeFlyoutItem.children"
          :key="child.key"
          :href="child.href"
          :class="{ active: isItemActive(child) }"
          @click="onFlyoutChildClick($event, child)"
        >
          {{ child.label }}
        </a>
      </div>
    </Teleport>
  </aside>
</template>

<script>
import { GetMyUserInfo, LogOut } from '@/actions/userActions.js'
import { useCookies } from 'vue3-cookies'
import 'dotenv/config'

export default {
  name: 'DeluxeSidebar',
  props: {
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  emits: ['toggle-collapse'],
  data() {
    return {
      userInfo: {},
      openDropdowns: {
        organisation: true,
        admin: false
      },
      activeFlyoutKey: '',
      flyoutStyle: {},
      projectUrl: process.env.VUE_APP_PROJECT_URL,
      boqUrl: process.env.VUE_APP_BOQ_URL,
      dotNetUrl: process.env.VUE_APP_DOTNET_URL
    }
  },
  computed: {
    accountTitle() {
      return this.userInfo.fullname || this.userInfo.firstName || 'User'
    },
    activeFlyoutItem() {
      return this.mainLinks.find((item) => item.key === this.activeFlyoutKey && item.flyout)
    },
    mainLinks() {
      return [
        {
          key: 'dashboard',
          label: this.$t('Dashboard'),
          icon: 'fa-solid fa-house',
          href: `${this.dotNetUrl}/project/dashboard`,
          external: true
        },
        {
          key: 'projects',
          label: this.$t('Projects'),
          icon: 'fa-regular fa-folder-closed',
          href: `${this.dotNetUrl}/project/list`,
          external: true
        },
        {
          key: 'organisation',
          label: this.$t('Organisation'),
          icon: 'fa-solid fa-user-tie',
          flyout: true,
          children: [
            { key: 'organisation-user', label: this.$t('User Directory'), href: '/Directory/User', route: true },
            { key: 'organisation-office', label: this.$t('Office Directory'), href: '/Directory/Office', route: true },
            { key: 'organisation-region', label: this.$t('Region Directory'), href: '/Directory/Region', route: true },
            { key: 'organisation-company', label: this.$t('Company Directory'), href: '/Directory/Company', route: true }
          ]
        },
        {
          key: 'cost-code',
          label: this.$t('Cost Code'),
          icon: 'fa-solid fa-file-invoice-dollar',
          href: '/Directory/CostCode',
          route: true
        },
        {
          key: 'bidding',
          label: this.$t('Bidding'),
          icon: 'fa-solid fa-laptop-file',
          href: `${this.boqUrl}/high-level-estimates`,
          external: true
        },
        {
          key: 'admin',
          label: this.$t('Administrative Page'),
          icon: 'fa-solid fa-wrench',
          flyout: true,
          children: [
            { key: 'admin-tax', label: this.$t('Tax Rules'), href: `${this.dotNetUrl}/admin/taxrules`, external: true },
            { key: 'admin-bank', label: this.$t('Bank Information'), href: `${this.dotNetUrl}/admin/bankinformation`, external: true },
            { key: 'admin-currency', label: this.$t('Currency Rates'), href: `${this.dotNetUrl}/admin/currency/rates`, external: true },
            { key: 'admin-signatures', label: this.$t('Staff Signatures'), href: `${this.dotNetUrl}/admin/signatures`, external: true }
          ]
        }
      ]
    }
  },
  mounted() {
    this.getMyInfo()
    document.addEventListener('click', this.closeFlyoutFromOutside)
    window.addEventListener('resize', this.closeFlyout)
    window.addEventListener('scroll', this.closeFlyout, true)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeFlyoutFromOutside)
    window.removeEventListener('resize', this.closeFlyout)
    window.removeEventListener('scroll', this.closeFlyout, true)
  },
  methods: {
    getMyInfo() {
      GetMyUserInfo().then((res) => {
        this.userInfo = res || {}
      })
    },
    itemClass(item) {
      return {
        active: this.isItemActive(item),
        'has-flyout': !!item.flyout,
        'sidebar-dropdown': !!item.children,
        'is-open': this.isGroupOpen(item)
      }
    },
    isGroupOpen(item) {
      if (!item.children) {
        return false
      }

      if (item.flyout) {
        return this.activeFlyoutKey === item.key || this.isItemActive(item)
      }

      return !!this.openDropdowns[item.key] || this.isItemActive(item)
    },
    isChevronOpen(item) {
      if (!item.children) {
        return false
      }

      if (item.flyout) {
        return this.activeFlyoutKey === item.key
      }

      return !!this.openDropdowns[item.key]
    },
    isItemActive(item) {
      if (item.children) {
        return item.children.some((child) => this.isItemActive(child))
      }

      return item.route && this.$route.path.toLowerCase() === item.href.toLowerCase()
    },
    onItemClick(event, item) {
      if (item.children) {
        event.preventDefault()
        event.stopPropagation()

        if (item.flyout) {
          this.toggleFlyout(item, event.currentTarget)
          return
        }

        this.closeFlyout()
        this.openDropdowns[item.key] = !this.openDropdowns[item.key]
        return
      }

      this.closeFlyout()
      if (item.route) {
        event.preventDefault()
        this.$router.push(item.href)
      }
    },
    toggleFlyout(item, trigger) {
      const shouldOpen = this.activeFlyoutKey !== item.key
      this.closeFlyout()

      if (!shouldOpen) {
        return
      }

      this.activeFlyoutKey = item.key
      this.positionFlyout(trigger)
    },
    positionFlyout(trigger) {
      if (!trigger) return

      const rect = trigger.getBoundingClientRect()
      const top = Math.min(rect.top, window.innerHeight - 220)

      this.flyoutStyle = {
        left: `${rect.right + 8}px`,
        top: `${Math.max(8, top)}px`,
        minWidth: '220px'
      }
    },
    closeFlyout() {
      this.activeFlyoutKey = ''
    },
    closeFlyoutFromOutside(event) {
      if (
        event.target.closest('.sidebar-flyout') ||
        event.target.closest('.deluxe-sidebar .has-flyout > a')
      ) {
        return
      }

      this.closeFlyout()
    },
    onFlyoutChildClick(event, child) {
      this.closeFlyout()
      this.onItemClick(event, child)
    },
    logoutUser() {
      const { cookies } = useCookies()

      LogOut().then(() => {
        cookies.remove('_auzer')
        this.$router.push({ name: 'SignOut', query: { signout: 'yes' } })
      })
    }
  }
}
</script>
